import * as Yup from "yup";

import { AccountContactType } from "@/components/Contracts/ConfirmationStep/Signature/signature.interface";
import { ValidateCNPJ } from "@/utils/ValidateCNPJ";
import { ValidateCPF } from "@/utils/ValidateCPF";

type AccountChecking = {
  bankNumber?: string;
  agencyNumber?: string;
  accountNumber?: string;
};

function buildCheckingAccountsSchema({
  requiredArray
}: {
  requiredArray: boolean;
}) {
  const itemSchema = Yup.object({
    bankNumber: Yup.string().required("O banco é obrigatório."),
    agencyNumber: Yup.string().required("A agência é obrigatória."),
    accountNumber: Yup.string().required("O número de conta é obrigatório.")
  }).test(
    "all-distinct",
    "Os dados bancários (número da conta, agência e banco) não podem ser iguais entre si.",
    value => {
      if (!value) return true;
      const { bankNumber, agencyNumber, accountNumber } = value;

      const accountEqualsAgency = accountNumber === agencyNumber;
      const agencyEqualsBank = agencyNumber === bankNumber;
      const bankEqualsAccount = bankNumber === accountNumber;

      if (accountEqualsAgency || agencyEqualsBank || bankEqualsAccount) {
        return false;
      }

      return true;
    }
  );

  let accountCheckingArraySchema = Yup.array().of(itemSchema);

  if (requiredArray) {
    accountCheckingArraySchema = accountCheckingArraySchema.min(
      1,
      "É obrigatório informar uma conta."
    );
  }

  accountCheckingArraySchema = accountCheckingArraySchema.test(
    "no-duplicate-combination",
    "Os números de conta devem ser únicos. Há contas duplicadas na lista.",
    (accounts: AccountChecking[]) => {
      if (!accounts) return true;
      const seen = new Set<string>();

      for (const account of accounts) {
        const { bankNumber, agencyNumber, accountNumber } = account;

        const key = `${bankNumber?.trim()}|${agencyNumber?.trim()}|${accountNumber?.trim()}`;

        if (seen.has(key)) return false;
        seen.add(key);
      }

      return true;
    }
  );

  return Yup.object({
    checkingAccounts: accountCheckingArraySchema
  });
}

const commonSchema = Yup.object({
  contacts: Yup.array()
    .of(
      Yup.object({
        contactType: Yup.string().required(
          "É obrigatório escolher um tipo de contato."
        ),
        contact: Yup.string().required("Os dados de contato são obrigatórios.")
      })
    )
    .required("Os dados de contato são obrigatórios."),
  addresses: Yup.array()
    .of(
      Yup.object({
        zipCode: Yup.string().required("O código postal é obrigatório."),
        city: Yup.string().required("A cidade é obrigatória."),
        state: Yup.string().required("O estado é obrigatório."),
        street: Yup.string().required("O logradouro é obrigatório."),
        neighborhood: Yup.string().required("O bairro é obrigatório."),
        number: Yup.string().required("O número é obrigatório."),
        note: Yup.string().nullable()
      })
    )
    .required("O código postal é obrigatório.")
});

const physicalAccountSchema = commonSchema.shape({
  documents: Yup.array()
    .of(
      Yup.object({
        documentType: Yup.string().required(
          "O tipo de documento é obrigatório."
        ),
        documentNumber: Yup.string()
          .required("O documento é obrigatório.")
          .when("documentType", ([documentType], schema) => {
            if (documentType === "CPF") {
              return schema
                .min(11, "O CPF precisa ter no mínimo 11 dígitos.")
                .test("cpfValidation", "CPF inválido", value =>
                  value ? ValidateCPF(value) : true
                );
            }

            if (documentType === "CNPJ") {
              return schema
                .min(14, "O CNPJ precisa ter no mínimo 14 caracteres.")
                .test("cnpjValidation", "CNPJ inválido", value =>
                  value ? ValidateCNPJ(value) : true
                );
            }

            return schema;
          })
      })
    )
    .required("O documento é obrigatório."),

  account: Yup.array().of(
    Yup.object({
      fullName: Yup.string()
        .matches(/^[^'"]*$/, "Aspas não são permitidas.")
        .required("Nome é obrigatório"),
      birthDate: Yup.string()
        .test(
          "age",
          "Você deve ter pelo menos 16 anos de idade",
          function (value) {
            if (!value) return true;
            const minAgeDate = new Date();
            minAgeDate.setFullYear(minAgeDate.getFullYear() - 16);

            return new Date(value) <= minAgeDate;
          }
        )
        .required("Data de nascimento é obrigatória."),
      genderStatus: Yup.string().required("Selecione uma opção de gênero."),
      maritalStatusId: Yup.string().required("O estado civil é obrigatório."),
      occupation: Yup.string().required("Ocupação é obrigatória.")
    })
  ),

  contacts: Yup.array().of(
    Yup.object({
      contactType: Yup.string().required("O tipo de contato é obrigatório."),
      contact: Yup.string()
        .required("A forma de contato é obrigatória.")
        .when("contactType", ([contactType], schema) => {
          if (contactType === AccountContactType.EMAIL) {
            return schema
              .email("Insira um email válido.")
              .required("O email é obrigatório.");
          }

          if (
            contactType === AccountContactType.PHONE_NUMBER ||
            contactType === AccountContactType.WHATSAPP
          ) {
            return schema
              .min(10, "O telefone precisa ter no mínimo 10 dígitos.")
              .required("O número de telefone é obrigatório.");
          }

          return schema;
        })
    })
  )
});

const legalAccountSchema = commonSchema.shape({
  documents: Yup.array()
    .of(
      Yup.object({
        documentType: Yup.string().required(
          "O tipo de documento é obrigatório."
        ),
        documentNumber: Yup.string()
          .required("O documento é obrigatório.")
          .when("documentType", ([documentType], schema) => {
            if (documentType === "CNPJ") {
              return schema
                .required("O documento é obrigatório.")
                .min(14, "O CNPJ precisa ter no mínimo 14 caracteres.")
                .test("cnpjValidation", "CNPJ inválido", value =>
                  value ? ValidateCNPJ(value) : true
                );
            }

            return schema;
          })
      })
    )
    .required("O documento é obrigatório."),
  legalRepresentatives: Yup.array()
    .of(
      Yup.object({
        fullName: Yup.string()
          .matches(/^[^'"]*$/, "Aspas não são permitidas.")
          .required("Campo Obrigatório."),
        email: Yup.string()
          .email("Email Inválido")
          .required("Campo Obrigatório."),
        document: Yup.string()
          .min(11, "O CPF precisa ter no mínimo 11 dígitos.")
          .test("cpfValidation", "CPF inválido", value => {
            if (value) {
              return ValidateCPF(value);
            } else {
              return true;
            }
          })
          .required("Campo Obrigatório."),
        birthDate: Yup.string()
          .test(
            "age",
            "Você deve ter pelo menos 16 anos de idade",
            function (value) {
              if (!value) return true;
              const minAgeDate = new Date();
              minAgeDate.setFullYear(minAgeDate.getFullYear() - 16);

              return new Date(value) <= minAgeDate;
            }
          )
          .required("Data de nascimento é obrigatória."),
        role: Yup.string().nullable()
      })
    )
    .required("Campo Obrigatório.")
});

export function getPhysicalAccountSchema({
  checkingAccountsRequired
}: {
  checkingAccountsRequired: boolean;
}) {
  return physicalAccountSchema.concat(
    buildCheckingAccountsSchema({
      requiredArray: checkingAccountsRequired
    })
  );
}

export function getLegalAccountSchema({
  checkingAccountsRequired
}: {
  checkingAccountsRequired: boolean;
}) {
  return legalAccountSchema.concat(
    buildCheckingAccountsSchema({
      requiredArray: checkingAccountsRequired
    })
  );
}
